import { cn } from "@/utils/cn";

import type { ColumnDef } from "./index";

/**
 * The column whose content becomes the card's footer.
 *
 * A convention rather than a per-column flag: every table in this codebase
 * already names its button column `actions`, and asking eight call sites to
 * re-declare what their columns are for is the per-table configuration this
 * layout exists to avoid.
 */
const ACTIONS_KEY = "actions";

/**
 * A table's rows as stacked cards, for screens too narrow to hold the grid.
 *
 * WHY: the table is `w-full` with percentage column widths, so the scroll zone
 * around it never actually scrolls — the columns just compress. A six-column
 * table on a 375px screen gave the actions column about 37px to hold two icon
 * buttons, and nothing was reachable by scrolling sideways because there was
 * no overflow to scroll.
 *
 * Built from the SAME `ColumnDef` list the table renders, so a column added
 * for the desktop grid appears here without anyone remembering to. The first
 * column is the card's heading — every table in this module leads with the
 * record's name — and `actions` becomes the footer.
 */
export const RowCards = <T,>({
  data,
  columns,
  getRowId,
  onRowClick,
  rowClassName,
  hasSelection,
  selectedIdSet,
  disabledIdSet,
  onSelectRow,
}: {
  data: T[];
  columns: ColumnDef<T>[];
  getRowId: (row: T, index: number) => string;
  onRowClick?: (row: T, index: number) => void;
  rowClassName?: (row: T, index: number) => string;
  /** Whether the table selects rows at all — `SelectionDef` itself is not
   *  needed here, only whether to draw a checkbox. */
  hasSelection: boolean;
  selectedIdSet: Set<string>;
  disabledIdSet: Set<string>;
  onSelectRow: (id: string) => void;
}) => {
  const [titleColumn, ...restColumns] = columns;
  const actionsColumn = restColumns.find((col) => col.key === ACTIONS_KEY);
  const detailColumns = restColumns.filter((col) => col.key !== ACTIONS_KEY);

  return (
    <ul className="flex flex-col gap-3 py-2">
      {data.map((row, rowIndex) => {
        const id = getRowId(row, rowIndex);
        const isSelected = selectedIdSet.has(id);

        return (
          <li key={id}>
            <div
              onClick={(e) => {
                const target = e.target as HTMLElement;
                // Same two guards as the table row: clicks from a portalled
                // dialog are not inside this card, and a cell that owns its
                // own click says so with `data-no-row-click`.
                if (!e.currentTarget.contains(target)) return;
                if (target.closest("[data-no-row-click]")) return;
                onRowClick?.(row, rowIndex);
              }}
              className={cn(
                "rounded-xs border border-border bg-surface p-3 flex flex-col gap-2",
                onRowClick && "cursor-pointer",
                isSelected && "border-primary bg-primary/5",
                rowClassName?.(row, rowIndex),
              )}
            >
              <div className="flex items-start gap-2">
                {hasSelection && (
                  <span data-no-row-click className="pt-0.5">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      disabled={disabledIdSet.has(id)}
                      onChange={() => onSelectRow(id)}
                      className="cursor-pointer accent-primary disabled:cursor-not-allowed disabled:opacity-40"
                    />
                  </span>
                )}
                <div className="min-w-0 flex-1 text-sm font-semibold text-foreground">
                  {titleColumn?.render
                    ? titleColumn.render(row, rowIndex)
                    : ((row as Record<string, unknown>)[titleColumn?.key ?? ""] as React.ReactNode)}
                </div>
              </div>

              <dl className="flex flex-col gap-1.5">
                {detailColumns.map((col) => (
                  <div key={col.key} className="flex items-start justify-between gap-3">
                    <dt className="shrink-0 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                      {col.label}
                    </dt>
                    <dd className="min-w-0 text-end text-xs text-foreground">
                      {col.render
                        ? col.render(row, rowIndex)
                        : ((row as Record<string, unknown>)[col.key] as React.ReactNode)}
                    </dd>
                  </div>
                ))}
              </dl>

              {actionsColumn && (
                <div className="flex justify-end border-t border-border pt-2">
                  {actionsColumn.render?.(row, rowIndex)}
                </div>
              )}
            </div>
          </li>
        );
      })}
    </ul>
  );
};
